import { css } from '@emotion/react'
import type { CSSInterpolation } from '@emotion/serialize'
import QuickReplyIcon from '@mui/icons-material/QuickreplyOutlined'
import {
    CSSProperties,
    Fragment,
    SyntheticEvent,
    memo,
    useCallback,
} from 'react'
import type {
    Badge,
    EmojiClubs,
    Message,
    UserBadge,
} from '../../../../../types/mfc'
import { FloaterSize } from '../../../../constants/floaters'
import { openQuickMenu } from '../../../../state/modals/state.confirmation'
import { openQuickReply } from '../../../../state/modals/state.floater'
import { USERSSTATE } from '../../../../state/state.users'
import {
    getModelColor,
    getUserColor,
    hexRGBA,
    lightenNormalized,
} from '../../../../utilities/colors'
import { safeObj } from '../../../../utilities/safe'
import IconButton from '../../../Buttons/IconButton'
import MessageIcon from '../../../Icons/Message'
import { getThemeSpacing } from '../../../styles/appTheme'
import APP_BREAKPOINTS, { maxWidth } from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import NewsMessage from '../../News/ChatRow'
import MessageClubShow from '../common/MessageClubShow'
import ProcessedMessage from '../common/ProcessedMessage'
import { resolveStyle, resolveTestId } from '../common/utilities'

const badgeStyles = css({
    display: 'inline-block',
    position: 'relative',
    minWidth: '1.75ch',
    backgroundColor: 'transparent',
    borderRadius: 5,
    zIndex: 2,
    transition: 'letter-spacing 0.2s, background-color 0.2s',
    '& a:link': {
        textDecoration: 'none',
    },
    '&:hover': {
        letterSpacing: 0,
        backgroundColor: palette.chatclubEmojiBackground,
    },
})

const badgeRootStyles = css({
    display: 'inline-block',
    position: 'absolute',
    left: -16,
    top: 0,
    width: '1.75ch',
    letterSpacing: '-1.2rem',
    whiteSpace: 'nowrap',
})
const badgeRootStylesFullWindow = css([
    badgeRootStyles,
    {
        display: 'none',
    },
])

const renderBadges = (
    name: string,
    badge_list: Badge[],
    emojiClubs: EmojiClubs,
    fullWindow: boolean,
) => (
    <span css={fullWindow ? badgeRootStylesFullWindow : badgeRootStyles}>
        {!!badge_list && !!emojiClubs && (
            <span css={badgeStyles}>
                {badge_list.map((badge, index) => (
                    <span key={`${emojiClubs[badge?.cid]?.slug}-${index}`}>
                        <a
                            href={`//share.myfreecams.com/m/${
                                emojiClubs[badge?.cid]?.slug
                            }`}
                            title={`${name} is a member of the '${
                                emojiClubs[badge?.cid]?.name
                            }' club on MFC Share.`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {decodeURIComponent(emojiClubs[badge?.cid]?.emoji)}
                        </a>
                    </span>
                ))}
            </span>
        )}
    </span>
)

const messageElementStyles = css({
    position: 'relative',
    '& img': { maxWidth: '100%' },
})
const chatCardStyles = css({
    position: 'relative',
    padding: 0,
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 0,
    wordBreak: 'break-word',
    margin: `${getThemeSpacing(1)} ${getThemeSpacing(3)}`,
    [maxWidth(APP_BREAKPOINTS.md)]: {
        margin: `${getThemeSpacing(1)} ${getThemeSpacing(2.25)}`,
    },
})
const chatCardStylesFullWindow = css([
    chatCardStyles,
    {
        margin: `${getThemeSpacing(0.625)} ${getThemeSpacing(1.5)}`,
        [maxWidth(APP_BREAKPOINTS.md)]: {
            margin: `${getThemeSpacing(0.625)} ${getThemeSpacing(1)}`,
        },
    },
])

const chatCardCommonStyles = css({
    '& a': {
        color: 'inherit !important',
        textDecoration: 'none',
        cursor: 'pointer',
    },
})

const chatCardNameStyles = css([
    chatCardCommonStyles,
    {
        position: 'relative',
    },
])

const chatCardDividerStyles = css([
    chatCardCommonStyles,
    {
        marginRight: 5,
    },
])

const chatCardMessageStyle = css({
    color: palette.commonWhite,
    fontWeight: 700,
    textShadow: '1px 0 0 #000, -1px 0 0 #000, 0 -1px 0 #000, 0 1px 0 #000',
})

const openQuickReplyStyles = css({
    padding: '8px 0',
    marginRight: getThemeSpacing(1.5),
    whiteSpace: 'normal',
    '& span:first-of-type': {
        maxWidth: 58,
        color: palette.contrastText,
    },
})

const inRoomPmStyles = css({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
})

const pmMessageHeaderStyles = css({
    flexGrow: 1,
    cursor: 'pointer',
})

const messageContainerStyles = css({
    display: 'flex',
    flexFlow: 'column nowrap',
})

const rootRoomMessageStyles = css({ display: 'inline-block', width: '100%' })

const getMessageBackgroundColor = ({
    pm,
    fullWindow,
    darkMode,
    me,
    userColor,
}) => {
    if (pm && !fullWindow) {
        // this is an inline PM in room chat and we're not in full window mode
        if (me) {
            // pm sent by me
            return palette.chatInlinePmBackgroundMe
        } else {
            // pm sent to me
            return palette.chatInlinePmBackground
        }
    } else {
        if (!fullWindow) {
            // if (userColor === '#000000') return palette.chatBackground
            if (darkMode) {
                return palette.chatBackground
                // return hexRGBA(userColor, 0.03)
            } else {
                return hexRGBA(userColor, 0.05)
            }
        } else {
            return palette.chatBackground
        }
    }
}

interface MessageElementProps {
    userBadges?: Array<UserBadge>
    emojiClubs?: EmojiClubs
    slug: string
    fullWindow: boolean
    isLounge: boolean
    clubshowActive: boolean
    styles: Record<string, CSSInterpolation>
    darkMode: boolean
    pinned?: boolean
    isMobile?: boolean
    isVertical?: boolean
    isQuickReplyOpen?: boolean
    message: Message

    onLoad(event: SyntheticEvent<HTMLImageElement>): void
    onOpenMenu(slug: string): void
    onOpenWhiteboard(identifier: string | number): void
    onQualifyPurchase(payload: any): void
}

const MessageElement = memo((props: MessageElementProps) => {
    const {
        slug,
        fullWindow,
        isLounge,
        userBadges,
        emojiClubs,
        clubshowActive,
        onLoad,
        onOpenMenu,
        onOpenWhiteboard,
        onQualifyPurchase,
        styles,
        darkMode,
        message,
        pinned,
        isVertical = false,
        isMobile = false,
        isQuickReplyOpen = false,
    } = props
    const {
        emote = false,
        timestamp,
        clubShow,
        chatCard,
        system,
        images,
        guest,
        text,
        news,
        html,
        from,
        to,
        pm,
        me,
        slug: newsSlug,
    } = message

    const user =
        USERSSTATE[pm && me ? to : from] ||
        (guest ? { name: guest, id: 0 } : ({} as any))

    const { color, name, chatterSlug = name, id, isModel = false } = user

    const openUserMenu = useCallback(
        (e) => {
            e.preventDefault()
            if (isModel) {
                onOpenMenu(chatterSlug)
            } else if (!guest) {
                openQuickMenu(
                    id,
                    {
                        x: e.clientX,
                        y: e.clientY,
                    },
                    fullWindow,
                )
            }
        },
        [chatterSlug, isModel, fullWindow],
    )
    const openPmQuickReply = useCallback(
        (e) => {
            e.stopPropagation()
            openQuickReply(id, { size: FloaterSize.Small })
        },
        [id],
    )

    if (news) {
        return (
            <NewsMessage
                images={images}
                news={news}
                text={text}
                timestamp={timestamp}
                id={Number(from) || user?.id}
                username={user?.name || newsSlug}
                onLoad={onLoad}
                html={html}
            />
        )
    }

    if (!system && !user) {
        return null
    }

    if (system || !user) {
        if (clubShow && !clubshowActive) {
            return null
        }
        const {
            note,
            shareTip,
            shareTipConfirmation,
            shareTipMessage,
            tip,
            tipColor,
            title,
            url,
        } = message
        const tipStyle: CSSProperties = {
            color: !fullWindow
                ? tipColor
                    ? lightenNormalized(tipColor, -0.6)
                    : undefined
                : (tipColor ??
                  (shareTip
                      ? '#66ff66'
                      : tip
                        ? palette.chatTipBackground
                        : palette.commonWhite)),
            backgroundColor: !fullWindow
                ? (tipColor ?? (shareTip ? '#66ff66' : undefined))
                : palette.chatBackground,
        }

        if (!!clubShow && !user.clubShow) {
            return null
        }

        return (
            <Fragment>
                <div css={messageElementStyles}>
                    <div
                        data-testid={resolveTestId(message)}
                        css={resolveStyle(message, styles)}
                        style={tipStyle}
                    >
                        {message.topic && <span>Room Topic: </span>}
                        {!!clubShow && !!user && (
                            <MessageClubShow
                                pinned={pinned}
                                message={message}
                            />
                        )}
                        {!clubShow && (
                            <ProcessedMessage
                                text={(emote ? '* ' : '') + text}
                                images={images}
                                tip={tip}
                                url={url}
                                slug={slug}
                                note={note}
                                fullWindow={fullWindow}
                                title={title}
                                system={system}
                                shareTip={shareTip}
                                shareTipMessage={shareTipMessage}
                                darkMode={darkMode}
                                shareTipConfirmation={shareTipConfirmation}
                                onLoad={onLoad}
                                onOpenWhiteboard={onOpenWhiteboard}
                                onQualifyPurchase={onQualifyPurchase}
                            />
                        )}
                    </div>
                </div>
            </Fragment>
        )
    }

    const badge_list =
        safeObj(
            (Array.isArray(userBadges) ? userBadges : []).find(
                (user) => user.uid === id,
            ),
        ).badges || null
    if (badge_list) {
        badge_list.sort((a, b) =>
            emojiClubs && emojiClubs[a.cid] && emojiClubs[b.cid]
                ? emojiClubs[a.cid].amt - emojiClubs[b.cid].amt
                : 0,
        )
    }

    // const showQuickMenu = useCallback(
    //     (event) => {
    //         // console.log('[showQuickMenu]', user.slug, menuTimeoutRef, {
    //         //     clientX: event.clientX,
    //         //     clientY: event.clientY,
    //         // })
    //         // this should set a timeout to delay the opening so someone moving their mouse a lot won't
    //         // trigger it unintentionally... maybe that date ref can be used as a timeout id
    //         menuTimeoutRef.current = setTimeout(() => {
    //             // console.log('[showQuickMenu] opening', user.slug)
    //             openQuickMenu(id, {
    //                 x: event.clientX,
    //                 y: event.clientY,
    //             })
    //         }, 133)
    //     },
    //     [id],
    // )
    // const hideQuickMenu = useCallback(() => {
    //     // console.log('[hideQuickMenu]', user.slug)
    //     if (menuTimeoutRef.current) {
    //         clearTimeout(menuTimeoutRef.current)
    //     }
    //     // close the quick menu
    //     closeQuickMenu()
    // }, [])

    const userColor = getUserColor(color)

    const shouldOpenQuickReply = pm && !me

    if (pm) {
        if (isQuickReplyOpen) return null
        const canOpenQuickReply = !isMobile || (isMobile && isVertical)
        const quickReplyText = canOpenQuickReply
            ? 'Open Quick Reply'
            : 'Open PM'

        return (
            <div
                css={inRoomPmStyles}
                style={{
                    backgroundColor: getMessageBackgroundColor({
                        pm,
                        fullWindow,
                        darkMode,
                        userColor,
                        me,
                    }),
                    color: getModelColor(userColor, darkMode || fullWindow),
                    borderBottom: !fullWindow
                        ? `1px solid ${palette.chatDivider}`
                        : 'none',
                }}
            >
                <div
                    css={[
                        resolveStyle(message, styles),
                        messageContainerStyles,
                    ]}
                    data-testid={resolveTestId(message)}
                >
                    <span
                        style={{
                            fontWeight: !fullWindow ? 600 : 700,
                            color:
                                emote && fullWindow
                                    ? palette.commonWhite
                                    : undefined,
                        }}
                        css={pmMessageHeaderStyles}
                        onClick={
                            !fullWindow && !user.isGuest ? openUserMenu : null
                        }
                    >
                        <span>
                            {me ? `PM to ${name}` : `PM from ${name}`}
                            {!emote && ':'}
                        </span>
                    </span>
                    <span
                        data-testid="Container__ProcessedMessage"
                        css={fullWindow ? chatCardMessageStyle : undefined}
                    >
                        <ProcessedMessage
                            text={text}
                            html={html}
                            images={images}
                            fullWindow={fullWindow}
                            slug={slug}
                            isLounge={isLounge}
                            darkMode={darkMode}
                            onLoad={onLoad}
                        />
                    </span>
                </div>
                {shouldOpenQuickReply && (
                    <IconButton
                        title={quickReplyText}
                        css={openQuickReplyStyles}
                        onClick={openPmQuickReply}
                        showLabel
                    >
                        {canOpenQuickReply ? (
                            <QuickReplyIcon data-testid="QuickreplyOutlinedIcon" />
                        ) : (
                            <MessageIcon />
                        )}
                        <span>{quickReplyText}</span>
                    </IconButton>
                )}
            </div>
        )
    }
    // console.log('[MessageElement]', slug, props)
    const isCharlesBot = user.slug === 'charlesbot'
    const isSpecial = (user.isModel || isCharlesBot) && !fullWindow
    const messageStyle = {
        margin: `${getThemeSpacing(isSpecial ? 1 : 0.5)} ${getThemeSpacing(
            pinned || isMobile ? 2.25 : fullWindow ? 1.5 : 3,
        )}`,
    }
    return (
        <div
            css={rootRoomMessageStyles}
            style={{
                backgroundColor: getMessageBackgroundColor({
                    pm,
                    fullWindow,
                    darkMode,
                    userColor,
                    me,
                }),
                color: getModelColor(userColor, darkMode || fullWindow),
                borderBottom: !fullWindow
                    ? `1px solid ${palette.chatDivider}`
                    : 'none',
            }}
        >
            {!!chatCard && (
                <div
                    css={fullWindow ? chatCardStylesFullWindow : chatCardStyles}
                    data-testid={resolveTestId(message)}
                    dangerouslySetInnerHTML={{ __html: text }}
                />
            )}
            {!chatCard && (
                <div
                    css={resolveStyle(message, styles)}
                    data-testid={resolveTestId(message)}
                    style={messageStyle}
                >
                    <span
                        style={{
                            fontWeight: !fullWindow ? 600 : 700,
                            color:
                                emote && fullWindow
                                    ? palette.commonWhite
                                    : undefined,
                        }}
                        css={chatCardNameStyles}
                    >
                        {!emote &&
                            !fullWindow &&
                            renderBadges(
                                name,
                                badge_list,
                                emojiClubs,
                                fullWindow,
                            )}
                        <a
                            href="#"
                            onClick={
                                !fullWindow && !user.isGuest
                                    ? openUserMenu
                                    : null
                            }
                        >
                            {emote ? `* ${name} ` : name}
                        </a>
                    </span>
                    {!emote && (
                        <span
                            style={{
                                fontWeight: !fullWindow ? 600 : 700,
                            }}
                            css={chatCardDividerStyles}
                        >
                            :
                        </span>
                    )}
                    <span
                        data-testid="Container__ProcessedMessage"
                        css={fullWindow ? chatCardMessageStyle : undefined}
                    >
                        <ProcessedMessage
                            text={text}
                            images={images}
                            fullWindow={fullWindow}
                            slug={slug}
                            isLounge={isLounge}
                            darkMode={darkMode}
                            onLoad={onLoad}
                        />
                    </span>
                </div>
            )}
        </div>
    )
})

export default MessageElement
